/**
 * Extract a human-readable message from an RTK Query error. `fetchBaseQuery`
 * places the parsed response body on `err.data`; some backends return coded
 * errors as `{ error: { code, message, details } }`, while stock Nest errors
 * use `{ statusCode, message, error }`. Falls back to the caller-provided text.
 *
 * The platform `ApiExceptionFilter` collapses validation failures to the
 * generic `"Request validation failed"` and puts the actionable, per-field
 * reasons in `details: [{ field, message }]`. Without surfacing those, a 400
 * is indistinguishable from any other rejection — so append them here.
 */

interface ErrorDetail {
  field?: unknown;
  message?: unknown;
}

interface ErrorBody {
  message?: string | string[];
  details?: unknown;
  error?: { code?: string; message?: string | string[]; details?: unknown };
}

/** `details[]` → `field: message` lines, ignoring malformed entries. */
function formatDetails(details: unknown): string[] {
  if (!Array.isArray(details)) return [];
  return details.flatMap((entry: unknown): string[] => {
    if (typeof entry === 'string') return entry.trim() ? [entry.trim()] : [];
    if (typeof entry !== 'object' || entry === null) return [];
    const { field, message } = entry as ErrorDetail;
    if (typeof message !== 'string' || !message.trim()) return [];
    return [typeof field === 'string' && field.trim() ? `${field.trim()}: ${message.trim()}` : message.trim()];
  });
}

export function apiErrorMessage(err: unknown, fallback: string): string {
  const data = (err as { data?: ErrorBody } | undefined)?.data;
  const message = data?.error?.message ?? data?.message;
  const details = formatDetails(data?.error?.details ?? data?.details);

  const headline = Array.isArray(message)
    ? message.filter((item) => typeof item === 'string' && item.trim()).join('; ')
    : typeof message === 'string' && message.trim()
      ? message
      : '';

  if (!headline) return details.length > 0 ? details.join('; ') : fallback;
  return details.length > 0 ? `${headline} — ${details.join('; ')}` : headline;
}